import { useState } from 'react';
import { useHistory } from 'react-router';
import { kebabCase } from 'lodash';

import { isOfType } from 'shared/helpers/utils';
import { StepInfo, Wizard } from 'shared/components/Wizard';
import { getHubSellingRoute } from 'shared/helpers/routes';

import { useQualificationContext } from 'shared/helpers/QualificationContext';

/* eslint-disable no-restricted-syntax */
import { ProxyImagesWizardStep } from './ProxyImagesWizardStep';
import { ProxyImage, MlsPhotos, HomeConditions } from '../helpers/types';
import { generatePath } from 'react-router-dom';
/* eslint-enable no-restricted-syntax */

interface ProxyImagesWizardProps {
  onComplete: (selectedIds: Array<string>) => void;
  homeConditions: HomeConditions[];
  mlsPhotos: MlsPhotos[] | null | undefined;
}

export enum MlsOption {
  yes = 'yes',
  no = 'no',
  skip = 'skip',
}

export type Selection = {
  mlsOption?: MlsOption;
  proxyImage?: ProxyImage;
};

type SelectedImages = Record<string, Selection | undefined>;

/**
 * Renders proxy images selection step in home conditions flow
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const ProxyImagesWizard: React.FC<ProxyImagesWizardProps> = ({
  onComplete,
  homeConditions,
  mlsPhotos,
}) => {
  const { addressUUID } = useQualificationContext();
  const history = useHistory();
  const [, setRandom] = useState<number>();
  const [selectedImages, setSelectedImages] = useState<SelectedImages>(() => {
    const selectedImages: SelectedImages = {};
    homeConditions.forEach((type) => {
      selectedImages[type.title] = undefined;
    });
    return selectedImages;
  });

  const updateStepImage = (
    type: HomeConditions,
    proxyImage?: ProxyImage,
    mlsOption?: MlsOption,
  ) => {
    setSelectedImages((prevImages) => {
      prevImages[type.title] = {
        proxyImage,
        mlsOption,
      };
      return prevImages;
    });
    setRandom(Math.random());
  };

  const steps: StepInfo[] = homeConditions
    .map((type, index) => {
      const mySteps: StepInfo[] = [];

      const mlsPhoto = mlsPhotos?.find((mlsPhoto) => mlsPhoto.title === type.title);
      mySteps.push({
        // eslint-disable-next-line react/display-name
        content: ({ goToNextStep }) => (
          <ProxyImagesWizardStep
            selection={selectedImages[type.title]}
            title={type.title}
            options={type.proxyImages}
            onSelect={(option, mlsOption) => updateStepImage(type, option, mlsOption)}
            mlsPhoto={mlsPhoto}
            goToNextStep={() => {
              if (index + 1 === homeConditions.length) {
                const selectedIds = Object.values(selectedImages)
                  .filter(isOfType)
                  .map((selection) => selection?.proxyImage?.id)
                  .filter((id): id is string => !!id);
                onComplete(selectedIds);
              } else {
                goToNextStep();
              }
            }}
          />
        ),
        analyticsName: `${kebabCase(type.title)}-image-step`,
      });
      return mySteps;
    })
    .flat();

  return (
    <Wizard
      steps={steps}
      title=""
      onClose={() => history.push(generatePath(getHubSellingRoute(), { addressUUID }))}
      hideFooter
    />
  );
};
